import type {
  BooleanResponse,
  CreateGroupRequest,
  Group,
  GroupPagedList,
  GroupPagedListResponse,
  GroupResponse,
  UpdateGroupRequest,
  UserGroupListParams,
  UserGroupPagedList,
  UserGroupPagedListResponse,
  UserGroupUsersListParams,
} from '@apiContract';

import { receivedDigitalApi } from './index';
import isEqual from 'lodash/fp/isEqual';

const groupsApi = receivedDigitalApi.injectEndpoints({
  endpoints: (builder) => ({
    getGroups: builder.query<GroupPagedList, UserGroupListParams>({
      query: (params) => ({
        url: 'UserGroup',
        params,
      }),
      providesTags: ['UserGroups'],
      transformResponse: (response: GroupPagedListResponse) => response.objectData,
    }),
    getGroupsForPick: builder.query<{ [key: number]: Group }, undefined>({
      query: () => ({
        url: 'UserGroup',
        params: {
          'Paging.ItemsPerPage': 100000,
          'Paging.PageNumber': 0,
        },
      }),
      providesTags: ['UserGroups'],
      transformResponse: (response: GroupPagedListResponse) => {
        if (!response.objectData.items) return {};
        return response.objectData.items?.reduce<{ [key: number]: Group }>((acc, item) => {
          if (item) {
            acc[item.id] = item;
          }
          return acc;
        }, {});
      },
    }),
    getGroupById: builder.query<Group, number>({
      query: (id) => `UserGroup/${id}`,
      providesTags: ['UserGroups'],
      transformResponse: (response: GroupResponse) => response.objectData,
    }),
    getGroupUsers: builder.query<UserGroupPagedList, UserGroupUsersListParams>({
      query: ({ id, ...params }) => ({
        url: `UserGroup/${id}/users`,
        params,
      }),
      providesTags: ['GroupUsers'],
      transformResponse: (response: UserGroupPagedListResponse) => response.objectData,
    }),
    createGroup: builder.mutation<Group, CreateGroupRequest>({
      query: (requestData) => ({
        url: 'UserGroup',
        method: 'POST',
        body: requestData,
      }),
      invalidatesTags: ['UserGroups', 'Users', 'User', 'UsersInfinite', 'UsersMin'],
      transformResponse: (response: GroupResponse) => response.objectData,
    }),
    updateGroup: builder.mutation<Group, UpdateGroupRequest>({
      query: (requestData) => ({
        url: 'UserGroup',
        method: 'PUT',
        body: requestData,
      }),
      invalidatesTags: ['UserGroups', 'GroupUsers', 'Users', 'User', 'UsersInfinite', 'UsersMin', 'PieceAutoActionConflicts'],
      transformResponse: (response: GroupResponse) => response.objectData,
    }),
    deleteGroup: builder.mutation<boolean, number>({
      query: (id) => ({
        url: `UserGroup`,
        method: 'DELETE',
        params: { id },
      }),
      invalidatesTags: ['UserGroups', 'Users', 'User', 'UsersInfinite', 'UsersMin'],
      transformResponse: (response: BooleanResponse) => response.objectData,
    }),
    deleteGroups: builder.mutation<boolean, number[]>({
      query: (requestData) => ({
        url: 'UserGroup/list',
        method: 'DELETE',
        body: requestData,
      }),
      invalidatesTags: ['UserGroups', 'Users', 'User', 'UsersInfinite', 'UsersMin'],
      transformResponse: (response: BooleanResponse) => response.objectData,
    }),
    infiniteLoaderGroups: builder.query<GroupPagedList, UserGroupListParams>({
          query: (requestParams) => ({
            url: 'UserGroup',
            params: requestParams,
          }),
          serializeQueryArgs: ({ queryArgs }) => {
            const filters = { ...queryArgs };
            delete filters['Paging.PageNumber'];
            delete filters['Paging.ItemsPerPage'];
            return JSON.stringify(filters);
          },
          merge: (currentCache, newItems, { arg }) => {
            if (arg['Paging.PageNumber'] === 0) {
              return newItems;
            }
    
            return {
              ...newItems,
              pageNumber: newItems.pageNumber,
              pageSize: newItems.pageSize,
              totalCount: newItems.totalCount,
              totalPages: newItems.totalPages,
              hasPreviousPage: newItems.hasPreviousPage,
              hasNextPage: newItems.hasNextPage,
              items: [...(currentCache.items || []), ...(newItems.items || [])],
            };
          },
          providesTags: ['GroupsInfinite'],
          transformResponse: (response: GroupPagedListResponse) => response.objectData,
          forceRefetch: ({ currentArg, previousArg }) => {
            if (currentArg?.['Paging.PageNumber'] !== previousArg?.['Paging.PageNumber']) {
              return true;
            }
    
            const { "Paging.PageNumber": currPage, ...currRest } = currentArg || {};
            const { "Paging.PageNumber": prevPage, ...prevRest } = previousArg || {};
    
            return !isEqual(currRest, prevRest);
          },
        }),
  }),
});

export const {
  useGetGroupsQuery,
  useUpdateGroupMutation,
  useGetGroupUsersQuery,
  useCreateGroupMutation,
  useDeleteGroupMutation,
  useGetGroupByIdQuery,
  useGetGroupsForPickQuery,
  useDeleteGroupsMutation,
  useInfiniteLoaderGroupsQuery,
} = groupsApi;
